import {
  BasketIcon,
  BellNotificationIcon,
  Box,
  Calendar1Icon,
  Calendar2Icon,
  ChartIcon,
  Chip,
  Divider,
  GKButton,
  Grid2,
  IconButton,
  InvoiceIcon,
  RedirectIcon,
  ReloadIcon,
  RightIcon,
  RupeeIcon,
  Show,
  StatusField,
  Typography,
  UserIcon,
} from "components";
import { ClientBasketInterface } from "interfaces/ClientBasket";
import { map } from "lodash";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { matchDate } from "utils/helpers";
import ClientExecutionModal from "./ClientExecutionModal";
import InvestmentDetailsModal from "./InvestmentDetailsModal";
import SubscriptionDetailsModal from "./SubscriptionDetailsModal";

const ClientEquityBasketCard = (props: ClientBasketInterface) => {
  const {
    basketId,
    basketName,
    basketLogo,
    firstSubscriptionDate,
    lastRenewalDate,
    nextRenewalDate,
    advisorName,
    companyLogo,
    isActive,
    portfolioId,
    domainName,
    version,
    basketType,
    isRebalancePending,
    managementStyle,
  } = props;

  const navigate = useNavigate();

  const [openExecutionModal, setOpenExecutionModal] = useState(false);

  const [openSubscriptionDetailsModal, setOpenSubscriptionDetailsModal] =
    useState(false);
  const [openInvestmentDetailsModal, setOpenInvestmentDetailsModal] =
    useState(false);

  const headerCard = [
    {
      title: "First Subscription Date:",
      logo: Calendar2Icon,
      value: firstSubscriptionDate,
    },
    {
      title: "Last Renewal Date:",
      logo: ReloadIcon,
      value: lastRenewalDate,
    },
    {
      title: "Next Renewal Date:",
      logo: Calendar1Icon,
      value: nextRenewalDate,
    },
  ];

  return (
    <>
      <Grid2
        container
        borderBottom={`solid 1px var(--neutral-100)`}
        pb={3}
        spacing={1}
      >
        <Grid2 size={12} display={"flex"} justifyContent={"space-between"}>
          <Box
            display={"flex"}
            gap={0.5}
            alignItems={"center"}
            bgcolor={"var(--neutral-50)"}
            p={1}
            borderRadius={2}
          >
            <BasketIcon color="var(--neutral-700)" width={12} height={12} />
            <Typography color="var(--neutral-700)" fontSize={12}>
              {managementStyle === "Advisor"
                ? "Advisor"
                : basketType === "Model Portfolio"
                  ? "Model"
                  : "Subscription"}
            </Typography>
          </Box>
          <Box
            display={"flex"}
            gap={0.5}
            alignItems={"center"}
            bgcolor={"var(--primary-50)"}
            p={1}
            borderRadius={10}
            component={"button"}
            border={"none"}
            sx={{
              cursor: "pointer",
              "&:hover": {
                bgcolor: "var(--primary-100)",
                transition: "all 0.3s",
              },
              visibility: isRebalancePending ? "visible" : "hidden",
            }}
            onClick={() => setOpenExecutionModal(true)}
          >
            <BellNotificationIcon
              color="var(--neutral-700)"
              width={12}
              height={12}
            />
            <Typography color="var(--primary-900)" fontSize={12}>
              Rebalance Available
            </Typography>
            <RightIcon color="var(--primary-900)" width={12} height={12} />
          </Box>
        </Grid2>
        <Grid2 size={1} display={"flex"} justifyContent={"center"}>
          <img
            src={basketLogo || `/Logos/errorImg.svg`}
            alt=""
            style={{
              borderRadius: 6,
              width: "66px",
              height: "66px",
              border: `solid 1px var(--neutral-100)`,
              padding: 4,
            }}
          />
        </Grid2>
        <Grid2 size={8} container spacing={1}>
          <Grid2 size={12} alignItems={"center"} display={"flex"} gap={1}>
            <Typography fontSize={16} fontWeight={600}>
              {basketName}
            </Typography>
            <Show.When isTrue={managementStyle !== "Advisor"}>
              <IconButton
                onClick={() => {
                  window.open(
                    `https://${domainName}.${window.location.host}/${basketType === "Model" ? "strategies" : "asset"}/${basketId}`,
                    "_blank",
                  );
                }}
              >
                <RedirectIcon height={14} width={14} />
              </IconButton>
            </Show.When>
            <StatusField
              statusString={isActive ? "ACTIVE" : "INACTIVE"}
              styles={{
                padding: "6px 12px",
                borderRadius: 6,
                background: isActive ? "#E8F3EC" : "#D5DEE5",
                color: isActive ? "#15803D" : "#697586",
                textTransform: "uppercase",
              }}
            />
          </Grid2>
          <Grid2 size={12} display={"flex"} alignItems={"center"} gap={2}>
            {map(headerCard, (dataItem) => {
              return (
                <Box display={"flex"} alignItems={"center"} gap={0.5}>
                  <dataItem.logo color={"var(--neutral-500)"} />
                  <Typography color={"var(--neutral-500)"} fontSize={12}>
                    {dataItem.title}
                  </Typography>
                  <Typography fontWeight={600} fontSize={12}>
                    {matchDate(dataItem?.value)}
                  </Typography>
                </Box>
              );
            })}
          </Grid2>
          <Grid2
            size={12}
            display={"flex"}
            alignItems={"center"}
            gap={1}
            mt={3}
          >
            <GKButton
              size="small"
              variant="outlined"
              disabled={managementStyle === "Advisor"}
              startIcon={<InvoiceIcon width={16} height={16} />}
              color="dark"
              onClick={() => setOpenSubscriptionDetailsModal(true)}
            >
              My Subscriptions
            </GKButton>
            <Show.When isTrue={basketType === "Model"}>
              <GKButton
                variant="outlined"
                color="dark"
                disabled={managementStyle === "Advisor"}
                startIcon={<RupeeIcon width={16} height={16} />}
                onClick={() => setOpenInvestmentDetailsModal(true)}
              >
                My Investments
              </GKButton>
            </Show.When>
            <GKButton
              variant="outlined"
              color="dark"
              startIcon={<ChartIcon width={16} height={16} />}
              onClick={() =>
                navigate(`/app/client/equity-basket/${portfolioId}/holdings`)
              }
            >
              My Portfolio
            </GKButton>
          </Grid2>
        </Grid2>
        <Grid2 size={1} display={"flex"} justifyContent={"center"}>
          <Divider
            orientation="vertical"
            sx={{
              height: "100%",
              borderColor: "var(--neutral-100)",
            }}
          />
        </Grid2>
        <Grid2 size={2} container alignItems={"center"}>
          <Grid2 size={12}>
            <Chip
              avatar={<UserIcon style={{ height: "16px", width: "16px" }} />}
              label="Managed by"
              variant="outlined"
              sx={{
                p: "6px",
                fontSize: 12,
                fontWeight: 500,
                background: "#ECECEC",
                borderRadius: 4,
              }}
            />
          </Grid2>
          <Grid2
            size={12}
            display={"flex"}
            gap={1}
            alignItems={"center"}
            justifyContent={"flex-start"}
          >
            <img
              src={companyLogo || `/Logos/errorImg.svg`}
              alt=""
              style={{
                borderRadius: 4,
                width: "55px",
                height: "55px",
                border: `solid 1px var(--neutral-100)`,
              }}
            />
            <div>
              <Typography
                fontSize={12}
                fontWeight={500}
                color={"var(--neutral-500)"}
              >
                Advisor Name:
              </Typography>
              <Typography fontWeight={600}>{advisorName || "-"}</Typography>
            </div>
          </Grid2>
        </Grid2>
      </Grid2>

      <Show.When isTrue={openSubscriptionDetailsModal}>
        <SubscriptionDetailsModal
          open={openSubscriptionDetailsModal}
          setOpen={setOpenSubscriptionDetailsModal}
          basketId={basketId}
        />
      </Show.When>

      <Show.When isTrue={openExecutionModal}>
        <ClientExecutionModal
          open={openExecutionModal}
          setOpen={setOpenExecutionModal}
          basketId={basketId}
          portfolioId={portfolioId}
        />
      </Show.When>

      <Show.When isTrue={openInvestmentDetailsModal}>
        <InvestmentDetailsModal
          open={openInvestmentDetailsModal}
          setOpen={setOpenInvestmentDetailsModal}
          basketId={basketId}
          basketDetails={{
            portfolioId: portfolioId,
            basketId: basketId,
            version: version,
            companyLogo: companyLogo,
          }}
        />
      </Show.When>
    </>
  );
};

export default ClientEquityBasketCard;
